import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const UserProfile: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      setMessage(null);
      setError(null);
      setLoading(true);
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to log out:', err);
      setError('Failed to log out');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-2xl mx-auto relative">
        {/* Background effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-violet-600 to-teal-600 rounded-2xl blur-lg opacity-30"></div>
        
        <div className="relative glassmorphism rounded-xl shadow-2xl border border-white/10 p-8">
          <div className="flex items-center mb-8">
            <div className="relative mr-6">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-teal-600 rounded-full blur opacity-60"></div>
              <div className="relative w-20 h-20 bg-gradient-to-r from-violet-800 to-purple-900 rounded-full border border-white/10 shadow-2xl flex items-center justify-center">
                {currentUser?.photoURL ? (
                  <img 
                    src={currentUser.photoURL} 
                    alt="Profile" 
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <svg className="w-10 h-10 text-teal-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-white mb-1">My Profile</h2>
              <p className="text-gray-400">Manage your account details</p>
            </div>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-300" role="alert">
              <span className="block">{error}</span>
            </div>
          )}
          
          {message && (
            <div className="mb-6 p-4 bg-green-900/20 border border-green-800/50 rounded-lg text-green-300" role="alert">
              <span className="block">{message}</span>
            </div>
          )}
          
          <div className="space-y-6">
            <div className="glassmorphism border border-white/5 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Account Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                  <div className="px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-lg">
                    {currentUser?.email}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Account Created</label>
                  <div className="px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-lg">
                    {currentUser?.metadata.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleDateString() : 'Unknown'}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Last Sign In</label>
                  <div className="px-4 py-3 bg-gray-800/50 border border-gray-700 text-white rounded-lg">
                    {currentUser?.metadata.lastSignInTime ? new Date(currentUser.metadata.lastSignInTime).toLocaleDateString() : 'Unknown'}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-center pt-6 border-t border-gray-700/30">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-gray-800/50 hover:bg-gray-700 text-white rounded-lg transition flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                Back to Dashboard
              </button>
              
              <button
                onClick={handleLogout}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white rounded-lg shadow-lg shadow-red-900/30 transition flex items-center disabled:opacity-70"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></div>
                    <span>Logging Out...</span>
                  </div>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                    </svg>
                    Log Out
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile; 